/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes, { oneOfType } from 'prop-types';

// Component imports
import { Button, Card, Checkbox, Col, Empty, Image, Pagination, Popconfirm, Row, Spin, Typography } from 'antd';
import { CopyOutlined, EyeOutlined } from '@ant-design/icons';
import ReactPlayer from 'react-player';
import MediaPreviewModal from '../Modals/MediaPreviewModal';
import { DeleteIcon } from '../Icons/DeleteIcon';

// Misc imports
import { copyTextToClipboard } from '../../utils/function/copyToClipboard';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useCreateMediaFileMutation, useDeleteMediaFileMutation } from '../../store/media/mediaApi';

// Constant declarations
const { Text } = Typography;

const MediaGridTable = ({
  page,
  pageSize,
  imageHeight,
  gridSize,
  selectedFiles,
  hasPreview,
  setSelectedFiles=() => {},
  handleDelete=() => {},
  urlSearchParams=() => {},
  onClose=() => {}
}) => {
  const { t } = useTranslation();
  const { orgId } = useParams();

  const [fileId, setFileId] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [localPageSize, setLocalPageSize] = useState(pageSize || 32);
  const { data: mediaFiles, isFetching: isFetchingMediaFiles } = getCurrentQueryState('fetchMediaFiles', { orgId });
  const [, { isLoading: isDeletingMediaFile }] = useDeleteMediaFileMutation({ fixedCacheKey: 'mediaFiles-delete' });
  const [, { isLoading: isCreatingMediaFile }] = useCreateMediaFileMutation({ fixedCacheKey: 'mediaFiles-create' });

  useEffect(() => {
    urlSearchParams(1, localPageSize);
  }, []);

  const onPreviewClose = () => {
    setFileId(null);
    setIsPreviewModalOpen(false);
  };

  const renderMask = (file) => {
    return (
      <Row className="ant-image-mask" style={{ border: 1, borderRadius: 8 }}>
        <Row wrap={false} gutter={[16, 16]} align="middle" className="flex-column">
          <Col>
            <Text ellipsis={{ tooltip: file.fileName }} className="pr-2 ml-2 mr-2 text-white">{file.fileName}</Text>
          </Col>

          <Row wrap={false} gutter={4} align="middle">
            <Col>
              <Button
                type="link"
                disabled={isFetchingMediaFiles || isCreatingMediaFile || isDeletingMediaFile}
                className="p-0 text-white"
                onClick={(e) => {
                  e.stopPropagation();
                  setFileId(file.id);
                  setIsPreviewModalOpen(true);
                }}
                icon={<EyeOutlined />}
              />
            </Col>

            <Col>
              <Button
                type="link"
                className="p-0 text-white"
                disabled={isFetchingMediaFiles || isCreatingMediaFile || isDeletingMediaFile}
                onClick={(e) => {
                  e.stopPropagation();
                  copyTextToClipboard(file.url);
                }}
                icon={<CopyOutlined />}
              />
            </Col>

            <Col>
              <Popconfirm
                title={t('Are you sure you want to delete this file?')}
                okText={t('Yes')}
                cancelText={t('No')}
                placement="topRight"
                onConfirm={(e) => {
                  e.stopPropagation();
                  handleDelete(file);
                }}
                onCancel={(e) => {
                  e.stopPropagation();
                }}
              >
                <Button
                  type="link"
                  className="p-0 text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  icon={<DeleteIcon />}
                  disabled={isFetchingMediaFiles || isCreatingMediaFile || isDeletingMediaFile}
                />
              </Popconfirm>
            </Col>
          </Row>
        </Row>
      </Row>
    );
  };

  return (
    <Fragment>
      {
        mediaFiles?.data && mediaFiles?.data?.length > 0 ? (
          <Fragment>
            <Row gutter={[16, 16]}>
              {
                mediaFiles?.data?.map((file) =>
                  <Col key={file.id} xs={24} sm={12} md={12} lg={8} xl={6} xxl={gridSize}>
                    <Card
                      hoverable
                      style={{ maxHeight: imageHeight }}
                      cover={
                        <Fragment>
                          {
                            selectedFiles ? (
                              <Checkbox
                                style={{ position: 'absolute', zIndex: 100, top: '5%', left: '5%' }}
                                checked={selectedFiles.includes(file.id)}
                                value=""
                                onChange= {() => {
                                  setSelectedFiles(prevSelected =>
                                    prevSelected.includes(file.id)
                                      ? prevSelected.filter(item => item !== file.id)
                                      : [...prevSelected, file.id]
                                  );
                                }}
                              />
                            ) : null
                          }

                          {
                            file.mimeType.match(/(mp4)$/) ? (
                              <Fragment>
                                <ReactPlayer
                                  url={file.url}
                                  key={file.id}
                                  onClick={() => {
                                    onClose(file);
                                    if (hasPreview) {
                                      setFileId(file.id);
                                      setIsPreviewModalOpen(true);
                                    }
                                  }}
                                  className="m-auto object-fit-cover border-radius-base"
                                  style={{ maxHeight: imageHeight }}
                                  width="100%"
                                />

                                {renderMask(file)}
                              </Fragment>
                            ) : (
                              <Image
                                align="middle"
                                src={file.url}
                                key={file.id}
                                className="w-100 m-auto object-fit-cover border-radius-base"
                                style={{ height: imageHeight }}
                                onClick={() => {
                                  onClose(file);
                                  if (hasPreview) {
                                    setFileId(file.id);
                                    setIsPreviewModalOpen(true);
                                  }
                                }}
                                preview={{
                                  visible: false,
                                  src: file.url,
                                  mask: renderMask(file)
                                }}
                                placeholder={
                                  <Row className="w-100">
                                    <Col flex="auto" className="text-center">
                                      <Spin className="mt-10 mb-8"/>
                                    </Col>
                                  </Row>
                                }
                              />
                            )
                          }

                          <MediaPreviewModal id={fileId} file={file} onPreviewClose={onPreviewClose} handleDelete={handleDelete} isDeletingMediaFile={isDeletingMediaFile} isPreviewModalOpen={isPreviewModalOpen} />
                        </Fragment>
                      }
                    />
                  </Col>
                )
              }
            </Row>

            <Row gutter={16}>
              <Col flex="auto" className="text-right mt-5">
                <Pagination
                  hideOnSinglePage={true}
                  defaultCurrent={page}
                  current={page}
                  pageSize={localPageSize}
                  showSizeChanger={!pageSize}
                  pageSizeOptions={[10, 20, 32, 50, 100]}
                  total={mediaFiles?.count}
                  showTotal={(total) => `${t('Total Files')}: ${total}`}
                  onChange={(page, pageSize) => {
                    setLocalPageSize(pageSize);
                    urlSearchParams(page, pageSize);
                  }}/>
              </Col>
            </Row>
          </Fragment>
        ) : (
          <Col>
            <Card className="mt-5">
              <Empty
                description={<Text type="secondary">{t('There are no media files available for this Workspace')}.</Text>}
              />
            </Card>
          </Col>
        )
      }
    </Fragment>
  );
};

MediaGridTable.propTypes = {
  page: PropTypes.number,
  pageSize: PropTypes.number,
  gridSize: PropTypes.number,
  selectedFiles: PropTypes.array,
  setSelectedFiles: PropTypes.func,
  handleDelete: PropTypes.func,
  urlSearchParams: PropTypes.func,
  onClose: PropTypes.func,
  imageHeight: oneOfType([PropTypes.string, PropTypes.number]),
  hasPreview: PropTypes.bool
};

export default MediaGridTable;
